import React, { useId } from 'react';
import { getTpmsTireColor, getTpmsTireTextColor, TireStatus } from 'utils/tpms';
import BatteryIndicator from 'components/Tire/BatteryIndicator';

interface CombinedThreeAxlesTenWheelsProps {
  pressureValues: Record<string, string | null>;
  temperatureValues: Record<string, string | null>;
  pressureStatusMap: Record<string, TireStatus>;
  temperatureStatusMap: Record<string, TireStatus>;
  batteryLow?: Record<string, boolean>;
  tireStaleMap?: Record<string, boolean>;
  width?: number | string;
  height?: number | string;
}

// 10x6 tri-axle: single front axle + two dual tandem rear axles
// (2 + 4 + 4 = 10 tires). viewBox: 0 0 160 210.
// p-index -> tireCode order matches
// views/TirePressure/util.ts#getOrderedTireCodes for patternName '10x6'.

const FRONT_W = 24;
const DUAL_W = 22;
const TIRE_H = 56;
const TIRE_HALF = 28;

const ROWS = [1, 77, 153];
const AXLE_MIDS = [29, 105, 181];

const tireConfig: Array<{
  id: string;
  x: number;
  y: number;
  w: number;
}> = [
  { id: 'p1', x: 12, y: ROWS[0], w: FRONT_W },
  { id: 'p2', x: 124, y: ROWS[0], w: FRONT_W },
  { id: 'p5', x: 0, y: ROWS[1], w: DUAL_W },
  { id: 'p3', x: 23, y: ROWS[1], w: DUAL_W },
  { id: 'p4', x: 115, y: ROWS[1], w: DUAL_W },
  { id: 'p6', x: 138, y: ROWS[1], w: DUAL_W },
  { id: 'p9', x: 0, y: ROWS[2], w: DUAL_W },
  { id: 'p7', x: 23, y: ROWS[2], w: DUAL_W },
  { id: 'p8', x: 115, y: ROWS[2], w: DUAL_W },
  { id: 'p10', x: 138, y: ROWS[2], w: DUAL_W },
];

const CombinedThreeAxlesTenWheels: React.FC<
  CombinedThreeAxlesTenWheelsProps
> = ({
  pressureValues,
  temperatureValues,
  pressureStatusMap,
  temperatureStatusMap,
  batteryLow,
  tireStaleMap,
  width = 170,
  height = 223,
}) => {
  const uid = useId().replace(/:/g, '');

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 160 210"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {tireConfig.map(({ id, x, y, w }) => (
          <clipPath key={id} id={`clip-${uid}-${id}`}>
            <rect x={x} y={y} width={w} height={TIRE_H} rx={3} />
          </clipPath>
        ))}
      </defs>

      {/* Front axle */}
      <line
        x1={12 + FRONT_W}
        y1={AXLE_MIDS[0]}
        x2={124}
        y2={AXLE_MIDS[0]}
        stroke="#4A5C72"
        strokeWidth="6"
      />
      {/* Axle 2: connects inner tires */}
      <line
        x1={23 + DUAL_W}
        y1={AXLE_MIDS[1]}
        x2={115}
        y2={AXLE_MIDS[1]}
        stroke="#4A5C72"
        strokeWidth="6"
      />
      {/* Axle 3: connects inner tires */}
      <line
        x1={23 + DUAL_W}
        y1={AXLE_MIDS[2]}
        x2={115}
        y2={AXLE_MIDS[2]}
        stroke="#4A5C72"
        strokeWidth="6"
      />
      {/* Center spine */}
      <line
        x1={80}
        y1={AXLE_MIDS[0]}
        x2={80}
        y2={AXLE_MIDS[2]}
        stroke="#4A5C72"
        strokeWidth="6"
      />

      {tireConfig.map(({ id, x, y, w }) => {
        const pKey = id;
        const pressureColor = getTpmsTireColor(
          pressureStatusMap[pKey] ?? TireStatus.NO_SENSOR,
          tireStaleMap?.[pKey]
        );
        const tempColor = getTpmsTireColor(
          temperatureStatusMap[pKey] ?? TireStatus.NO_SENSOR,
          tireStaleMap?.[pKey]
        );
        const pressureTextColor = getTpmsTireTextColor(
          pressureStatusMap[pKey] ?? TireStatus.NO_SENSOR,
          tireStaleMap?.[pKey]
        );
        const tempTextColor = getTpmsTireTextColor(
          temperatureStatusMap[pKey] ?? TireStatus.NO_SENSOR,
          tireStaleMap?.[pKey]
        );
        const pVal = pressureValues[pKey] ?? '--';
        const tVal = temperatureValues[pKey] ?? '--';
        const cx = x + w / 2;
        const pyCenter = y + TIRE_HALF / 2;
        const tyCenter = y + TIRE_HALF + TIRE_HALF / 2;

        return (
          <g key={id}>
            <g clipPath={`url(#clip-${uid}-${id})`}>
              <rect
                x={x}
                y={y}
                width={w}
                height={TIRE_HALF}
                fill={pressureColor}
              />
              <rect
                x={x}
                y={y + TIRE_HALF}
                width={w}
                height={TIRE_HALF}
                fill={tempColor}
              />
            </g>
            <line
              x1={x}
              y1={y + TIRE_HALF}
              x2={x + w}
              y2={y + TIRE_HALF}
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="0.5"
            />
            <text
              x={cx}
              y={pyCenter}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={pressureTextColor}
              fontSize={9}
              fontWeight={600}
              style={{ pointerEvents: 'none' }}
            >
              {pVal}
            </text>
            <text
              x={cx}
              y={tyCenter}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={tempTextColor}
              fontSize={9}
              fontWeight={600}
              style={{ pointerEvents: 'none' }}
            >
              {tVal}
            </text>
            <BatteryIndicator
              cx={cx}
              cy={y}
              tireBottomY={y + TIRE_H}
              tireWidth={w}
              show={batteryLow?.[pKey]}
              fullCover
            />
          </g>
        );
      })}
    </svg>
  );
};

export default CombinedThreeAxlesTenWheels;
